/** @jsxRuntime classic */
/** @jsx jsx */

import {
  MouseEvent,
  ReactNode,
  useLayoutEffect,
  useState,
  useRef,
} from 'react';
import { BuilderBlocks } from '@builder.io/react';
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Tabs, TabsItemType } from '@zapier/design-system';
import { mediaQueries } from '../../../constants';
import {
  EventAction,
  InteractionName,
  SectionName,
  useAvroTracking,
  useTrackEvent,
  CtaGoal,
  CtaName,
  EventName,
} from '../../../observability';
import { isBrowser } from '../../../utils';
import { innerText } from '../../../utils/innerText';
import { Section } from '../../Section/Section';
import { BuilderComponentProps } from '../../../builder';

interface Props extends BuilderComponentProps {
  changeUrlOnTabChange?: boolean;
  heading: string;
  subheading: string;
  tabs: {
    heading: string;
    content: ReactNode;
    id?: string;
  }[];
}

const Styles = labelStyles('TabsSection', {
  navContent: css({
    overflowX: 'auto',
  }),

  tabContent: css({
    overflowX: 'auto',
    paddingTop: '30px',

    [mediaQueries.tablet]: {
      paddingTop: '45px',
    },

    [mediaQueries.desktop]: {
      paddingTop: '55px',
    },
  }),
});

export function TabsSection(props: Props) {
  const root = useRef<HTMLDivElement>(null);

  const { navItems, content } = props.tabs.reduce(
    (
      result: { navItems: TabsItemType[]; content: Record<string, ReactNode> },
      tab,
      index,
    ) => {
      const id = tab.id || tab.heading.toLowerCase().replace(/\s/g, '-');
      const href = `#${id}`;
      return {
        navItems: [...result.navItems, { href, text: tab.heading }],
        content: {
          ...result.content,
          [href]: (
            <BuilderBlocks
              blocks={tab.content}
              dataPath={`component.options.tabs.${index}.content`}
              parentElementId={props.builderBlock.id}
            />
          ),
        },
      };
    },
    { navItems: [], content: {} },
  );

  const [selectedTabHref, setSelectedTabHref] = useState(
    isBrowser() &&
      window.location.hash &&
      Object.prototype.hasOwnProperty.call(content, window.location.hash)
      ? window.location.hash
      : navItems[0].href,
  );

  useLayoutEffect(() => {
    if (
      window.location.hash &&
      Object.prototype.hasOwnProperty.call(content, window.location.hash) &&
      root.current
    ) {
      root.current.scrollIntoView();
    }
  }, [content]);

  const trackEvent = useTrackEvent();
  const { emitUserInteractionEvent } = useAvroTracking();

  const handleTabChange = (tab: TabsItemType, event: MouseEvent) => {
    event.preventDefault();

    setSelectedTabHref(tab.href);

    if (props.changeUrlOnTabChange) {
      window.location.hash = tab.href;
    }

    emitUserInteractionEvent({
      event_action: EventAction.click,
      interaction_name: InteractionName.tab_change,
      interaction_raw_text: innerText(tab.text),
      option_selected: innerText(tab.text),
      custom_tracking_fields: {
        section_name: SectionName.TabsSection,
      },
    });

    trackEvent(EventName.CALL_TO_ACTION, {
      path: window.location.pathname,
      from_path: document.referrer,
      cta_name: CtaName.tabSelection,
      cta_raw_text: tab.text,
      cta_goal: CtaGoal.getMoreInformation,
    });
  };

  return (
    <div ref={root}>
      <Section
        heading={props.heading}
        layerName={props.builderBlock?.layerName}
        sectionToTrack={SectionName.TabsSection}
        subheading={props.subheading}
      >
        <div css={Styles.navContent}>
          <Tabs
            items={navItems}
            onChooseItem={handleTabChange}
            selectedItemHref={selectedTabHref}
          />
        </div>
        <div css={Styles.tabContent}>{content[selectedTabHref]}</div>
      </Section>
    </div>
  );
}
